/**
 * useWPHealthGate - Gate hook for pre-creation WP health check
 *
 * Wraps any content creation action with a WordPress site health probe.
 * If the site is unreachable, shows a warning dialog. If reachable (or if
 * the health check itself fails), proceeds directly.
 *
 * Usage:
 *   const { triggerWithHealthCheck, isChecking, warningProps } = useWPHealthGate(executeCreate);
 *   // Call triggerWithHealthCheck() instead of executeCreate() directly
 *   // Render <WPHealthWarningDialog {...warningProps} />
 */

import { useState, useCallback } from 'react';
import { blogCreationService } from '../services/blogCreationService';

export function useWPHealthGate(onProceed: () => void | Promise<void>) {
  const [showWarning, setShowWarning] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [tenantId, setTenantId] = useState('');

  const triggerWithHealthCheck = useCallback(async () => {
    setIsChecking(true);
    try {
      const result = await blogCreationService.checkWPHealth();
      setTenantId(result.tenant_id);
      if (!result.reachable) {
        setIsChecking(false);
        setShowWarning(true);
        return;
      }
    } catch {
      // If health check itself fails (network error etc.), proceed anyway
      // — generation-time check is the safety net
      console.warn('[WP_HEALTH] Pre-check failed, proceeding');
    }
    setIsChecking(false);
    await onProceed();
  }, [onProceed]);

  const warningProps = {
    open: showWarning,
    onOpenChange: setShowWarning,
    onProceedAnyway: () => {
      setShowWarning(false);
      onProceed();
    },
    tenantId,
  };

  return { triggerWithHealthCheck, isChecking, warningProps };
}
